import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  BookOpen,
  Copy,
  Facebook,
  HeartPulse,
  Images,
  Instagram,
  LayoutGrid,
  PenLine,
  Link2,
  ListTodo,
  MessageCircle,
  MessageSquare,
  Palette,
  Pause,
  Play,
  RefreshCw,
  Settings,
  ShoppingBag,
  Sparkles,
  StickyNote,
  Trash2,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { api, CarouselItem, type CarouselFbPublishMode, type CarouselInspirationBorrow, type CarouselLayoutQa, type CarouselPublishChannel } from "@/lib/api";
import { TONE_CLASS, type SettingsTone } from "@/lib/carouselSettingsMeta";
import { copyText, packFromItem } from "@/lib/carouselGenerationPack";
import {
  recommendCta,
  SOURCE_EMPTY,
  STATUS_TIPS,
  STUDIO_TAB_META,
  STUDIO_TIPS,
  houseCanonState,
  studioTabFromHash,
  studioTabToHash,
  type AbVariable,
  type BgMode,
  type CloseMode,
  type CtaIntent,
  type StudioTab,
} from "@/lib/carouselStudioMeta";
import { resolveBriefFromDraft } from "@/lib/carouselBriefResolve";
import { type ConceptsTraceOutgoing } from "@/lib/carouselGenerationPack";
import PageHeader from "@/components/PageHeader";
import AiJobModal, { type AiJobKind, type AiJobParam } from "@/components/AiJobModal";
import { AiActionButton, BriefPreview, CopyOutcomePanel, CtaIo, StudioProcessChain, StudioSplit, StudioTabsList } from "@/components/studioTabs";
import { StudioInfoStrip, StudioKindChip, StudioNowBar, StudioTrackChip } from "@/components/studioVoice";
import { nextActionFor, type PublicationKind, type StudioTrack } from "@/lib/studioVoice";
import { StudioModalBody, StudioModalFooter, StudioModalFrame, StudioModalHeader } from "@/components/StudioModalFrame";
import { DesignAnalysisCard, DesignLayoutNoteCard, HouseCanonBar, StudioOptionCard } from "@/components/DesignReviewPanel";
import CarouselReelPanel from "@/components/CarouselReelPanel";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { emitInlineMarkdown, type TextMark } from "@/lib/carouselTextMarks";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { InfoTip, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import BriefTab from "./tabs/BriefTab";
import DraftTraceTab from "./tabs/DraftTraceTab";
import CopyTab from "./tabs/CopyTab";
import RenderTab from "./tabs/RenderTab";
import PublishTab from "./tabs/PublishTab";
import RetroTab from "./tabs/RetroTab";
import WynikiTab from "./tabs/WynikiTab";
import { useCarouselQueries } from "./hooks/useCarouselQueries";
import { useCarouselMutations } from "./hooks/useCarouselMutations";
import { FinalSlidesModal, slideHasCopy, tabForExisting, TipBadge } from "./helpers";

const DEFAULT_BORROW: CarouselInspirationBorrow = {
  layout: true,
  idea: true,
  hierarchy: true,
  colors: false,
  photo_kind: false,
};

const BORROW_CHIPS: { id: keyof CarouselInspirationBorrow; label: string; tip: string }[] = [
  { id: "layout", label: "Układ", tip: "Schemat i liczba slajdów." },
  { id: "idea", label: "Pomysł", tip: "Rodzaj haka i kąta, nie ich zdania." },
  { id: "hierarchy", label: "Hierarchia", tip: "Wielki tytuł, numer albo stos cytatów." },
  { id: "colors", label: "Kolory", tip: "Tylko niuans na kremie Kasi, nie klon palety." },
  { id: "photo_kind", label: "Rodzaj zdjęcia", tip: "Portret / faktura / życie w pickerze. Bez ich fotek." },
];

function pct(n?: number) {
  if (n == null || Number.isNaN(n)) return "";
  return `${(n * 100).toFixed(1)}%`;
}

function formatDesignQa(review?: { ok?: boolean; verdict?: string; diagnosis?: string; pass?: number } | null): string {
  if (!review || (!review.verdict && review.ok == null)) return "";
  if (review.ok === false || review.verdict === "fix") {
    return review.diagnosis ? `Design: ${review.diagnosis}` : "Design do poprawki";
  }
  if (review.verdict === "ok" || review.ok) {
    return review.pass ? `Design OK · tura ${review.pass}` : "Design OK";
  }
  return "";
}

function formatLayoutQa(qa?: CarouselLayoutQa | null): string {
  if (!qa || qa.ok == null) return "";
  if (qa.summary) return qa.summary;
  if (qa.ok) return "Skład OK";
  const failed = qa.failed || [];
  if (!failed.length) return "Skład do poprawki";
  const indexes = [...new Set(failed.map((row) => row.index).filter(Boolean))].join(", ");
  const reasons = [...new Set(failed.map((row) => row.reason).filter(Boolean))].join(" / ");
  return indexes && reasons ? `Slajd ${indexes}: ${reasons}` : reasons || `Slajd ${indexes}`;
}

const STATUS_LABEL: Record<string, string> = {
  idea: "pomysły",
  draft: "szkic",
  approved: "zatwierdzona",
  scheduled: "zaplanowana",
  publishing: "publikacja",
  published: "opublikowana",
  failed: "błąd",
};

export default function KaruzelePage() {
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const [pain, setPain] = useState(searchParams.get("pain") || "");
  const [book, setBook] = useState("");
  const [tool, setTool] = useState("");
  const [sku, setSku] = useState("");
  const [formatId, setFormatId] = useState("");
  const [czasotekstFormId, setCzasotekstFormId] = useState("personal_arc");
  const [styleId, setStyleId] = useState("");
  const [visualPackId, setVisualPackId] = useState("");
  const [bgMode, setBgMode] = useState<BgMode>("flat");
  const [bgHint, setBgHint] = useState("");
  const [bgPresetId, setBgPresetId] = useState("");
  const [bgPlacementId, setBgPlacementId] = useState("");
  const [styleApplyMode, setStyleApplyMode] = useState<"bg" | "full" | "layout">("bg");
  const [urls, setUrls] = useState("");
  const [note, setNote] = useState("");
  const [borrow, setBorrow] = useState<CarouselInspirationBorrow>({ ...DEFAULT_BORROW });
  const [draftText, setDraftText] = useState("");
  const [draftEditMode, setDraftEditMode] = useState<"literal" | "hook_close" | "reach">("hook_close");
  const [composeMode, setComposeMode] = useState<"" | "operator" | "ai">("");
  const [intentKind, setIntentKind] = useState<PublicationKind>("");
  const [forcePick, setForcePick] = useState(false);
  const [useBook, setUseBook] = useState(true);
  const [useComments, setUseComments] = useState(true);
  const [useMessenger, setUseMessenger] = useState(true);
  const [useStories, setUseStories] = useState(true);
  const [funnel, setFunnel] = useState<"" | "tofu" | "mofu" | "bofu">("");
  const [ctaIntent, setCtaIntent] = useState<CtaIntent | "">("");
  const [closeMode, setCloseMode] = useState<CloseMode | "">("");
  const [abVariable, setAbVariable] = useState<AbVariable>("");
  const [abPairId, setAbPairId] = useState("");
  const [ctaTouched, setCtaTouched] = useState(false);
  const [painPicked, setPainPicked] = useState(Boolean(searchParams.get("pain")));
  const [formatPicked, setFormatPicked] = useState(false);
  const [funnelPicked, setFunnelPicked] = useState(false);
  const [skuPicked, setSkuPicked] = useState(false);
  const [stylePicked, setStylePicked] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [when, setWhen] = useState("");
  const [publishChannel, setPublishChannel] = useState<CarouselPublishChannel>("both");
  const [fbPublishMode, setFbPublishMode] = useState<CarouselFbPublishMode>("gallery");
  const [caption, setCaption] = useState("");
  const [captionFeedback, setCaptionFeedback] = useState("");
  const [verifyCta, setVerifyCta] = useState(true);
  const [copyFeedback, setCopyFeedback] = useState("");
  const [copySlides, setCopySlides] = useState<Record<string, unknown>[]>([]);
  const copyMdRef = useRef<Record<string, string>>({});
  const [finalPreview, setFinalPreview] = useState<null | "approve" | "publish">(null);
  const [composeIndex, setComposeIndex] = useState<number | null>(null);
  const [qaOverride, setQaOverride] = useState(false);
  const [traceOut, setTraceOut] = useState<ConceptsTraceOutgoing | null>(null);
  const [traceId, setTraceId] = useState<string | null>(null);
  const [studioTab, setStudioTab] = useState<StudioTab>(() =>
    typeof window !== "undefined" ? studioTabFromHash(window.location.hash) : "brief",
  );
  const [aiJob, setAiJob] = useState<AiJobKind | null>(null);

  const { catalogQ, sourcesQ, statusQ, listQ, rankingQ, musicStatusQ, proposeMusicQ, slotQ, currentQ } = useCarouselQueries({
    pain, book, tool, useBook, useComments, useMessenger, useStories, aiJob, selectedId,
  });
  const slot = slotQ.data;

  const item = currentQ.data;
  const itemMode = item?.compose_mode === "operator" || item?.compose_mode === "ai" ? item.compose_mode : "";
  const itemKind: PublicationKind =
    item?.kind === "carousel" || item?.kind === "feed_post" || item?.kind === "czasotekst"
      ? item.kind
      : "";
  const activeMode = itemMode || composeMode;
  const effectiveKind: PublicationKind = itemKind || intentKind;
  const isOperator = activeMode === "operator";
  const isAiTrack = activeMode === "ai";
  const studioTrack = (activeMode === "operator" || activeMode === "ai" ? activeMode : "") as StudioTrack;
  const nowAction = nextActionFor(studioTab, studioTrack, item, { draftText });
  const showPicker = forcePick || !activeMode || !effectiveKind;
  const catalog = catalogQ.data;
  const studio = statusQ.data;
  const copyApproved = Boolean((item?.copy_approved_at || "").trim());
  const layoutQa = item?.layout_qa;
  const designQa = item?.design_review;
  const houseCanon = houseCanonState(item?.design_review, Boolean(item?.slides?.length));
  const qaFailed = houseCanon.score
    ? houseCanon.ok === false
    : layoutQa?.ok === false || designQa?.ok === false || designQa?.verdict === "fix";
  const qaBlocks = Boolean(qaFailed) && !qaOverride;
  const qaLabel = [formatLayoutQa(layoutQa), formatDesignQa(designQa)].filter(Boolean).join(" · ");
  const hasComposedSlides = Boolean(item?.slides?.length);
  const composeSlide =
    composeIndex != null
      ? (item?.slides || []).find((s) => Number(s.index_n) === Number(composeIndex))
      : undefined;
  const hasCopySlides = copySlides.length > 0;
  const hasSelectedIdea = Boolean((item?.selected_idea_id || "").trim());
  const selectedIdea = (item?.ideas || []).find((idea) => idea.id === item?.selected_idea_id || idea.selected);

  useEffect(() => {
    const slides = item?.copy?.slides;
    if (!item?.id) {
      copyMdRef.current = {};
      setCopySlides([]);
      return;
    }
    copyMdRef.current = {};
    setCopySlides(Array.isArray(slides) ? slides.map((slide) => ({ ...slide })) : []);
    setCaption(item.caption || "");
    setCaptionFeedback(item.caption_feedback || "");
    if (item.caption_verify != null) setVerifyCta(Boolean(item.caption_verify));
    setCopyFeedback(item.copy_feedback || "");
    if (item.draft_text) setDraftText(item.draft_text);
    if (item.draft_edit_mode === "literal" || item.draft_edit_mode === "hook_close" || item.draft_edit_mode === "reach") {
      setDraftEditMode(item.draft_edit_mode);
    }
    if (item.inspiration_borrow) setBorrow({ ...DEFAULT_BORROW, ...item.inspiration_borrow });
    if (item.cta_intent || item.close_mode) {
      setCtaIntent((item.cta_intent as CtaIntent) || "");
      setCloseMode((item.close_mode as CloseMode) || "");
      setCtaTouched(true);
    }
    if (item.ab_variable != null) setAbVariable((item.ab_variable as AbVariable) || "");
    if (item.ab_pair_id != null) setAbPairId(item.ab_pair_id || "");
    if (item.bg_mode) setBgMode(item.bg_mode === "gemini" ? "gemini" : "flat");
    if (item.bg_hint != null) setBgHint(item.bg_hint || "");
    if (item.bg_preset_id != null) setBgPresetId(item.bg_preset_id || "");
    if (item.bg_placement_id != null) setBgPlacementId(item.bg_placement_id || "");
    if (item.czasotekst_form_id) setCzasotekstFormId(item.czasotekst_form_id);
    if (item.kind === "carousel" || item.kind === "feed_post" || item.kind === "czasotekst") {
      setIntentKind(item.kind);
    }
    if (item.compose_mode === "operator" || item.compose_mode === "ai") {
      setComposeMode(item.compose_mode);
    }
    if (item.style_apply_mode != null) {
      const m = item.style_apply_mode;
      setStyleApplyMode(m === "full" || m === "layout" ? m : "bg");
    }
    if (item.channel === "ig" || item.channel === "fb" || item.channel === "both") {
      setPublishChannel(item.channel);
    }
    setFbPublishMode(item.fb_publish_mode || "gallery");
  }, [item?.id, item?.selected_idea_id, item?.llm_status, item?.caption, item?.caption_feedback, item?.caption_verify, item?.copy?.slides, item?.copy_feedback, item?.draft_text, item?.draft_edit_mode, item?.inspiration_borrow, item?.cta_intent, item?.close_mode, item?.ab_variable, item?.ab_pair_id, item?.bg_mode, item?.bg_hint, item?.bg_preset_id, item?.bg_placement_id, item?.czasotekst_form_id, item?.style_apply_mode, item?.channel, item?.fb_publish_mode]);

  useEffect(() => {
    setQaOverride(false);
  }, [selectedId]);

  const recCta = recommendCta(pain, formatId);
  const effectiveIntent = ctaTouched && ctaIntent ? ctaIntent : recCta.cta_intent;
  const effectiveClose = ctaTouched && closeMode ? closeMode : recCta.close_mode;

  const goTab = (next: StudioTab) => {
    setStudioTab(next);
    const hash = studioTabToHash(next);
    if (typeof window !== "undefined" && window.location.hash !== hash) {
      window.history.replaceState(null, "", hash);
    }
  };

  const briefBody = (mode: "operator" | "ai", kind: "carousel" | "feed_post" | "czasotekst" = "carousel") => {
    const resolved = resolveBriefFromDraft(
      draftText,
      {
        pain,
        formatId,
        funnel,
        sku,
        styleId,
        ctaTouched,
        ctaIntent,
        closeMode,
        painPicked,
        formatPicked,
        funnelPicked,
        skuPicked,
        stylePicked,
      },
      catalog?.pains,
    );
    return {
      book_id: useBook ? book : "",
      pain_id: resolved.pain_id,
      tool_id: useBook ? tool : "",
      sku: resolved.sku,
      style_id: resolved.style_id,
      visual_pack_id: visualPackId || undefined,
      format_id: resolved.format_id,
      use_book: useBook,
      use_comments: useComments,
      use_messenger: useMessenger,
      use_stories: useStories,
      kind,
      funnel_stage: resolved.funnel_stage,
      czasotekst_form_id: kind === "czasotekst" ? czasotekstFormId || undefined : undefined,
      inspiration_urls: urls
        .split(/\n|,/)
        .map((u) => u.trim())
        .filter(Boolean),
      inspiration_note: note,
      inspiration_borrow: borrow,
      draft_text: draftText,
      draft_edit_mode: draftEditMode,
      compose_mode: mode,
      cta_intent: resolved.cta_intent,
      close_mode: kind === "carousel" ? resolved.close_mode : "caption_only",
      ab_variable: kind === "carousel" ? abVariable : "",
      ab_pair_id: kind === "carousel" ? abPairId : "",
      bg_mode: bgMode,
      bg_preset_id: bgPresetId || undefined,
      bg_placement_id: bgPlacementId || undefined,
      style_apply_mode: styleApplyMode,
    };
  };

  const mergeCopySlides = (base = copySlides) =>
    base.map((slide, idx) => {
      const text = copyMdRef.current[`${idx}:text`];
      const sub = copyMdRef.current[`${idx}:sub`];
      if (text == null && sub == null) return slide;
      const next = { ...slide };
      if (text != null) {
        next.text = text;
        next.heading = text;
        next.text_marks = [];
      }
      if (sub != null) {
        next.sub = sub;
        next.body = sub;
        next.sub_marks = [];
      }
      return next;
    });

  const {
    searchMusicM,
    reelM,
    createM,
    ctaM,
    borrowM,
    analyzeInspM,
    uploadInspM,
    abortLlmM,
    selectM,
    generateCopyM,
    reviseCopyM,
    applyCopyReviewM,
    copyChatM,
    copyReviewOnDemandM,
    copyChatUndoM,
    fromDraftM,
    captionM,
    captionChatUndoM,
    composeModeM,
    patchCopyM,
    renderM,
    cancelRenderM,
    approveM,
    recomposeM, syncSlideCopyM,
    layoutQaM,
    designReviewM,
    designFeedbackM,
    designApplyM,
    designFixM,
    scheduleM,
    republishM,
    publishPlatformM,
    retryPublishM,
    fromPubM,
    pauseM,
    deleteM,
    ratingM,
    retroM,
    layoutPlanM,
    templateM,
    czasotekstCopyM,
  } = useCarouselMutations({
    selectedId,
    qc,
    DEFAULT_BORROW,
    activeMode,
    setSelectedId,
    setTraceId,
    setForcePick,
    setComposeMode,
    setIntentKind,
    briefBody,
    setCaption,
    setCopySlides,
    setBorrow,
    traceId,
    copyFeedback,
    draftText,
    draftEditMode,
    captionFeedback,
    verifyCta,
    item,
    copySlides,
    mergeCopySlides,
    copyMdRef,
    bgHint,
    caption,
    when,
    setFinalPreview,
    goTab,
    setStyleId,
    setVisualPackId,
    setFormatId,
    setBgMode,
    setAiJob,
    isOperator,
    publishChannel,
    fbPublishMode
  });

  const copyBusy =
    item?.llm_status === "discuss" ||
    item?.llm_status === "copy" ||
    item?.llm_status === "review" ||
    item?.llm_status === "copy_chat" ||
    item?.llm_status === "layout_plan" ||
    item?.llm_status === "draft_critique" ||
    item?.llm_status === "draft_place" ||
    item?.llm_status === "draft_caption" ||
    item?.llm_status === "caption_cta" ||
    generateCopyM.isPending ||
    reviseCopyM.isPending ||
    applyCopyReviewM.isPending ||
    copyChatM.isPending ||
    copyReviewOnDemandM.isPending ||
    copyChatUndoM.isPending ||
    fromDraftM.isPending;

  const commitCopyField = (idx: number, field: "text" | "sub", next: string) => {
    copyMdRef.current[`${idx}:${field}`] = next;
    const slides = mergeCopySlides();
    setCopySlides(slides);
    if (selectedId) patchCopyM.mutate(slides);
  };

  const publishBusy =
    item?.status === "publishing" ||
    item?.publish_progress?.phase === "publishing" ||
    scheduleM.isPending ||
    republishM.isPending ||
    publishPlatformM.isPending ||
    retryPublishM.isPending;

  const askDelete = (row: CarouselItem) => {
    const label = row.short_id || row.title || "tę karuzelę";
    const msg =
      row.status === "published"
        ? `Usunąć ${label} z kolejki? Post na IG/FB zostaje.`
        : row.status === "scheduled"
          ? `Usunąć zaplanowaną ${label} z kolejki? Nie opublikuje się.`
          : `Usunąć ${label} z kolejki?`;
    if (window.confirm(msg)) deleteM.mutate(row.id);
  };

  const sourceHits = sourcesQ.data?.items || [];
  const bookHits = sourceHits.filter((hit) => hit.type === "book");
  const voiceHits = sourceHits.filter((hit) => hit.type === "voice");
  const commentHits = sourceHits.filter((hit) => hit.type === "comment");
  const messengerHits = sourceHits.filter((hit) => hit.type === "messenger");
  const storyHits = sourceHits.filter((hit) => hit.type === "story" || hit.kind === "story");
  const bookSectionHits = [...bookHits, ...voiceHits];
  const catalogTypes = catalog?.types || catalog?.formats || [];
  const traceItem = item?.id && item.id === traceId ? item : undefined;
  const catalogLabel = (
    rows: { id?: string; label?: string }[] | undefined,
    id: string,
    fallback = "",
  ) => rows?.find((row) => row.id === id)?.label || fallback || id;

  const pickTrack = (mode: "operator" | "ai") => {
    setForcePick(false);
    if (selectedId && itemMode && itemMode !== mode) {
      composeModeM.mutate(mode);
      return;
    }
    setComposeMode(mode);
  };

  const openExisting = (row: CarouselItem) => {
    setSelectedId(row.id);
    setForcePick(false);
    setComposeMode(row.compose_mode === "operator" || row.compose_mode === "ai" ? row.compose_mode : "ai");
    setIntentKind(
      row.kind === "carousel" || row.kind === "feed_post" || row.kind === "czasotekst" ? row.kind : "carousel",
    );
    setCaption(row.caption || "");
    goTab(tabForExisting(row));
  };

  useEffect(() => {
    if (studioTab === "draft" && effectiveKind && effectiveKind !== "carousel") {
      goTab("copy");
    }
  }, [studioTab, effectiveKind]);

  useEffect(() => {
    const apply = () => setStudioTab(studioTabFromHash(window.location.hash));
    window.addEventListener("hashchange", apply);
    return () => window.removeEventListener("hashchange", apply);
  }, []);

  useEffect(() => {
    if (item?.status === "publishing") {
      goTab("publish");
      return;
    }
    if (item?.status === "published" && item?.llm_status === "retro" && studioTab === "publish") {
      goTab("retro");
    }
  }, [item?.status, item?.llm_status, studioTab]);

  useEffect(() => {
    if (!when && slot?.local) setWhen(slot.local);
  }, [slot?.local, when]);

  const resetStudio = () => {
    setSelectedId(null);
    setComposeMode("");
    setIntentKind("");
    setForcePick(false);
    setTraceId(null);
    setAiJob(null);
    setCopySlides([]);
    setCaption("");
    setCopyFeedback("");
    goTab("brief");
  };

  const changeTrack = () => {
    const dirty = Boolean(item?.ideas?.length || hasCopySlides || item?.draft_text);
    if (dirty && !window.confirm("Zmiana typu lub przebiegu czyści karty albo wstawiony tekst. Kontynuować?")) return;
    setForcePick(true);
    setComposeMode("");
    setIntentKind("");
    setSelectedId(null);
    setAiJob(null);
  };

  const startConcepts = () => {
    createM.reset();
    setComposeMode("ai");
    setIntentKind("carousel");
    setTraceId(null);
    setTraceOut({
      pain,
      book,
      tool,
      sku,
      formatId,
      styleId,
      useBook,
      useComments,
      useMessenger,
      useStories,
      urls,
      note,
      painLabel: pain ? catalogLabel(catalog?.pains, pain) : "wszystkie",
      bookLabel: book ? catalogLabel(catalog?.books, book) : "wszystkie książki",
      toolLabel: tool ? catalogLabel(catalog?.tools, tool) : "bez filtra",
      skuLabel: catalogLabel(catalog?.skus, sku, sku),
      formatLabel: formatId ? catalogLabel(catalogTypes, formatId) : "mieszanka",
      styleLabel: catalogLabel(catalog?.styles, styleId, styleId),
      sources: sourceHits,
    });
    setAiJob("concepts");
    createM.mutate("carousel");
  };

  const startCzasotekst = () => {
    createM.reset();
    czasotekstCopyM.reset();
    setIntentKind("czasotekst");
    const runLlm = activeMode === "ai";
    createM.mutate("czasotekst", {
      onSuccess: (res) => {
        const id = res.item?.id;
        if (!id) return;
        if (runLlm) {
          setAiJob("caption");
          czasotekstCopyM.mutate(id);
        }
      },
    });
  };

  const startPost = () => {
    createM.reset();
    setIntentKind("feed_post");
    createM.mutate("feed_post");
  };

  const llmIs = (...statuses: string[]) => Boolean(item?.llm_status && statuses.includes(item.llm_status));
  const conceptsRunning =
    (createM.isPending && (createM.variables == null || createM.variables === "carousel")) ||
    llmIs("concepts", "inspiration");
  const inspirationRunning = analyzeInspM.isPending || llmIs("inspiration");
  const captionJobSteps = item?.llm_progress?.job_steps || [];
  const captionOnlyJob =
    captionJobSteps[0] === "draft_caption" && !captionJobSteps.includes("draft_place");
  const captionRunning =
    captionM.isPending ||
    czasotekstCopyM.isPending ||
    llmIs("czasotekst_copy") ||
    ((llmIs("draft_caption") || llmIs("caption_cta")) && (aiJob === "caption" || captionOnlyJob));
  const fromDraftRunning =
    fromDraftM.isPending ||
    llmIs("draft_critique") ||
    llmIs("draft_place") ||
    (isOperator && llmIs("layout_plan")) ||
    (llmIs("draft_caption") && !captionOnlyJob && aiJob !== "caption");
  const layoutPlanRunning = layoutPlanM.isPending || (!isOperator && llmIs("layout_plan"));
  const copyRunning = generateCopyM.isPending || llmIs("discuss", "copy", "review");
  const copyReviseRunning = reviseCopyM.isPending;
  const renderRunning =
    renderM.isPending ||
    item?.render_status === "queued" ||
    item?.render_status === "rendering";
  const layoutQaRunning = layoutQaM.isPending || llmIs("layout_qa");
  const designReviewRunning = designReviewM.isPending || llmIs("design_review");
  const designApplyRunning = designApplyM.isPending || item?.render_status === "design" || llmIs("design_verify");
  const designFixRunning =
    designFixM.isPending ||
    item?.render_status === "design" ||
    llmIs("design_review", "design_verify");
  const retroRunning = retroM.isPending || llmIs("retro");
  const publishRunning =
    item?.status === "publishing" ||
    item?.publish_progress?.phase === "publishing" ||
    scheduleM.isPending ||
    republishM.isPending ||
    publishPlatformM.isPending ||
    retryPublishM.isPending;
  const reelRunning =
    reelM.isPending || item?.reel_status === "queued" || item?.reel_status === "rendering";

  const jobRunning = (kind: AiJobKind) =>
    ({
      concepts: conceptsRunning,
      inspiration: inspirationRunning,
      fromDraft: fromDraftRunning,
      caption: captionRunning,
      layoutPlan: layoutPlanRunning,
      copy: copyRunning && !copyReviseRunning,
      copyRevise: copyReviseRunning,
      render: renderRunning,
      layoutQa: layoutQaRunning,
      designReview: designReviewRunning,
      designApply: designApplyRunning,
      houseFix: designFixRunning,
      retro: retroRunning,
      publish: publishRunning,
      post: createM.isPending && (createM.variables === "feed_post" || createM.variables === "czasotekst"),
      reel: reelRunning,
    })[kind];

  const clickAi = (kind: AiJobKind, start?: () => void) => {
    if (kind === "fromDraft" || kind === "layoutPlan") {
      goTab("draft");
      setAiJob(kind);
      if (start && !jobRunning(kind)) start();
      return;
    }
    if (kind === "publish") {
      goTab("publish");
      if (start && !jobRunning(kind)) start();
      return;
    }
    if (
      kind === "render" ||
      kind === "houseFix" ||
      kind === "designReview" ||
      kind === "designApply" ||
      kind === "layoutQa"
    ) {
      goTab("render");
      if (kind === "render") setComposeIndex(null);
      setAiJob(kind);
      if (start && !jobRunning(kind)) start();
      return;
    }
    setAiJob(kind);
    if (start && !jobRunning(kind)) start();
  };

  const aiJobParams = (): AiJobParam[] => {
    const model = item?.llm_progress?.model || "";
    const painLabel = pain ? catalogLabel(catalog?.pains, pain) : "wszystkie";
    const formatLabel = formatId ? catalogLabel(catalogTypes, formatId) : "mieszanka";
    const styleLabel = catalogLabel(catalog?.styles, styleId, styleId);
    const base: AiJobParam[] = [
      { label: "Model", value: model },
      { label: "Ból", value: painLabel },
      { label: "Typ", value: formatLabel },
      { label: "Styl", value: styleLabel },
      { label: "Notatka", value: note.trim() || "brak" },
    ];
    if (aiJob === "copy" || aiJob === "copyRevise") {
      return [
        ...base,
        { label: "Karta", value: selectedIdea?.hook || "brak" },
        { label: "Uwaga", value: copyFeedback.trim() || "brak" },
      ];
    }
    if (aiJob === "render") {
      return [
        ...base,
        { label: "Tło", value: (item?.bg_mode || bgMode) === "gemini" ? "Gemini" : "druk" },
        { label: "Preset tła", value: bgPresetId || "auto" },
        { label: "Umiejscowienie", value: bgPlacementId || "uniform" },
        {
          label: "Zakres stylu",
          value:
            styleApplyMode === "full"
              ? "tlo+layout"
              : styleApplyMode === "layout"
                ? "tylko layout"
                : "tylko tlo",
        },
        { label: "Hint", value: bgHint.trim() || "brak" },
      ];
    }
    if (aiJob === "houseFix") {
      return [
        ...base,
        { label: "Weźmie", value: "złożoną serię" },
        { label: "Zwróci", value: "żywy pasek, ETA i zapytanie do modelu" },
      ];
    }
    if (aiJob === "inspiration") {
      return [...base, { label: "URL", value: urls.trim() || "brak" }];
    }
    if (aiJob === "fromDraft" || aiJob === "layoutPlan") {
      return [...base, { label: "Szkic", value: draftText.trim() ? `${draftText.trim().length} znaków` : "brak" }];
    }
    if (aiJob === "caption") {
      return [
        ...base,
        { label: "Uwaga", value: captionFeedback.trim() || "brak" },
        { label: "Książki", value: verifyCta ? "sprawdzam CTA" : "bez weryfikacji" },
      ];
    }
    if (aiJob === "publish") {
      return [...base, { label: "Slot", value: when || "teraz" }];
    }
    if (aiJob === "reel") {
      return [
        ...base,
        { label: "Utwór", value: item?.reel_track_title || "Epidemic" },
        { label: "FB", value: "rolka 9:16" },
        { label: "IG", value: "karuzela" },
      ];
    }
    return base;
  };

  const aiStatusLabel = () => {
    if (czasotekstCopyM.isError && aiJob === "caption") {
      return (czasotekstCopyM.error as Error).message || "Błąd czasotekstu.";
    }
    if (createM.isError && (aiJob === "concepts" || aiJob === "post")) {
      return (createM.error as Error).message || "Błąd składania.";
    }
    if (fromDraftM.isError && aiJob === "fromDraft") {
      return (fromDraftM.error as Error).message || "Błąd wstawiania szkicu.";
    }
    if (item?.llm_status === "error") return item.llm_detail || "Błąd modelu.";
    if (aiJob === "render" && item?.render_status === "failed") return item.render_error || "Błąd renderu.";
    if (aiJob === "reel" && item?.reel_status === "failed") return item.reel_error || "Błąd rolki.";
    if (aiJob === "publish" && item?.status === "failed") return item.publish_error || "Błąd publikacji.";
    if (item?.llm_detail) return item.llm_detail;
    if (item?.pipeline?.now) return item.pipeline.now;
    if (aiJob && jobRunning(aiJob)) return "W toku…";
    return "Gotowe. Wynik jest w bloku Wyjście.";
  };

  const aiFailed =
    Boolean(czasotekstCopyM.isError && aiJob === "caption") ||
    Boolean(createM.isError && (aiJob === "concepts" || aiJob === "post")) ||
    Boolean(fromDraftM.isError && aiJob === "fromDraft") ||
    item?.llm_status === "error" ||
    (aiJob === "render" && item?.render_status === "failed") ||
    (aiJob === "reel" && (item?.reel_status === "failed" || reelM.isError)) ||
    (aiJob === "houseFix" && designFixM.isError) ||
    (aiJob === "caption" && (captionM.isError || czasotekstCopyM.isError)) ||
    (aiJob === "publish" && item?.status === "failed");

  const aiError =
    czasotekstCopyM.isError && aiJob === "caption"
      ? (czasotekstCopyM.error as Error).message
      : createM.isError && (aiJob === "concepts" || aiJob === "post")
      ? (createM.error as Error).message
      : fromDraftM.isError && aiJob === "fromDraft"
        ? (fromDraftM.error as Error).message
      : renderM.error && aiJob === "render"
        ? renderM.error.message
        : reelM.error && aiJob === "reel"
          ? (reelM.error as Error).message
        : designFixM.isError && aiJob === "houseFix"
          ? (designFixM.error as Error).message
          : captionM.isError && aiJob === "caption"
            ? (captionM.error as Error).message
            : scheduleM.error && aiJob === "publish"
            ? (scheduleM.error as Error).message
            : "";

  const aiAbort =
    aiJob === "render"
      ? () => cancelRenderM.mutate()
      : aiJob && aiJob !== "publish"
        ? () => abortLlmM.mutate()
        : undefined;

  return (
    <div className="karuzele-page -mx-8 -my-8 min-h-screen">
      <header className="border-b border-slate-200 bg-white px-6 py-6 lg:px-8">
        <div>
          <PageHeader
            title="Kreator publikacji"
            subtitle="Najpierw typ i tor (szkic albo AI). Potem tylko kroki adekwatne do wybranej publikacji: karuzela, post albo czasotekst."
            actions={
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => pauseM.mutate(!studio?.queue_paused)}
                >
                  {studio?.queue_paused ? <Play className="mr-2 h-3.5 w-3.5" /> : <Pause className="mr-2 h-3.5 w-3.5" />}
                  {studio?.queue_paused ? "Wznów kolejkę" : "Wstrzymaj kolejkę"}
                </Button>
                <Button size="sm" variant="ghost" asChild>
                  <Link to="/karuzele/ustawienia">
                    <Settings className="mr-2 h-3.5 w-3.5" />
                    Ustawienia
                  </Link>
                </Button>
                <Button size="sm" variant="outline" onClick={resetStudio}>
                  Nowa
                </Button>
                <Button size="sm" variant="ghost" onClick={() => listQ.refetch()}>
                  <RefreshCw className="mr-2 h-3.5 w-3.5" />
                  Odśwież
                </Button>
              </div>
            }
          />
          <StudioInfoStrip className="mt-2 items-center">
            <StudioKindChip kind={effectiveKind} />
            <StudioTrackChip track={studioTrack} />
            {effectiveKind === "czasotekst" ? (
              <span className="inline-flex items-center rounded-md border border-violet-200 bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-900">
                Forma:{" "}
                {(catalog?.czasotekst_forms || []).find((f) => f.id === (item?.czasotekst_form_id || czasotekstFormId))
                  ?.label ||
                  item?.czasotekst_form_id ||
                  czasotekstFormId}
              </span>
            ) : null}
            <TipBadge
              tip={STUDIO_TIPS.gemini}
              variant={studio?.gemini_ready ? "success" : "destructive"}
              icon={Sparkles}
            >
              Gemini {studio?.gemini_ready ? "gotowe" : "brak klucza"}
            </TipBadge>
            <TipBadge
              tip={STUDIO_TIPS.igPublish}
              variant={studio?.publish?.can_publish_ig ? "brand" : "outline"}
              icon={Instagram}
            >
              IG publish {studio?.publish?.can_publish_ig ? "OK" : `brak: ${(studio?.publish?.missing_ig || []).join(", ") || "scope"}`}
            </TipBadge>
            <TipBadge
              tip={studio?.publish?.fb_hint || STUDIO_TIPS.fbPublish}
              variant={
                !studio?.publish?.can_publish_fb
                  ? "outline"
                  : studio?.publish?.using_page_token
                    ? "success"
                    : "destructive"
              }
              icon={Facebook}
            >
              FB publish{" "}
              {!studio?.publish?.can_publish_fb
                ? `brak: ${(studio?.publish?.missing_fb || []).join(", ") || "scope"}`
                : studio?.publish?.using_page_token
                  ? "OK (Page)"
                  : "scope OK, brak Page tokena"}
            </TipBadge>
            <TipBadge
              tip={STUDIO_TIPS.queue}
              variant={studio?.queue_paused ? "destructive" : "success"}
              icon={ListTodo}
            >
              {studio?.queue_paused ? "kolejka wstrzymana" : "kolejka aktywna"}
            </TipBadge>
          </StudioInfoStrip>
          <StudioProcessChain pipeline={item?.pipeline} />
        </div>
      </header>

      <div className="px-6 py-6 lg:px-8">
      <Tabs value={studioTab} onValueChange={(value) => goTab(value as StudioTab)}>
        <div className="mb-4 space-y-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <StudioTabsList pipeline={item?.pipeline} item={item} kind={effectiveKind} />
            <p className="text-sm text-muted-foreground">
              {item ? (
                <>
                  <span className="font-medium text-foreground">{item.title || item.hook || item.short_id}</span>
                  {item.pipeline?.cost_label ? ` · ${item.pipeline.cost_label}` : ""}
                </>
              ) : (
                STUDIO_TAB_META.find((tab) => tab.id === studioTab)?.hint
              )}
            </p>
          </div>
          <StudioNowBar
            action={nowAction}
            currentTab={studioTab}
            onGo={(tab) => goTab(tab)}
          />
        </div>

        <BriefTab ctx={{ BORROW_CHIPS, DEFAULT_BORROW, STATUS_LABEL, abPairId, abVariable, abortLlmM, activeMode, aiAbort, aiError, aiFailed, aiJob, aiJobParams, aiStatusLabel, analyzeInspM, approveM, askDelete, bgHint, bgPresetId, bgPlacementId, styleApplyMode, bgMode, book, bookHits, bookSectionHits, borrow, borrowM, briefBody, cancelRenderM, caption, captionFeedback, captionJobSteps, captionM, captionChatUndoM, captionOnlyJob, captionRunning, catalog, catalogLabel, catalogQ, catalogTypes, changeTrack, clickAi, closeMode, commentHits, commitCopyField, composeIndex, composeMode, composeModeM, composeSlide, conceptsRunning, copyApproved, copyBusy, copyFeedback, copyMdRef, copyReviseRunning, copyRunning, copySlides, createM, ctaIntent, ctaM, ctaTouched, painPicked, formatPicked, funnelPicked, skuPicked, stylePicked, setPainPicked, setFormatPicked, setFunnelPicked, setSkuPicked, setStylePicked, czasotekstFormId, setCzasotekstFormId, intentKind, setIntentKind, effectiveKind, currentQ, deleteM, designApplyM, designApplyRunning, designFeedbackM, designFixM, designFixRunning, designQa, designReviewM, designReviewRunning, draftText, draftEditMode, effectiveClose, effectiveIntent, finalPreview, forcePick, formatId, fromDraftM, fromDraftRunning, fromPubM, funnel, generateCopyM, goTab, hasComposedSlides, hasCopySlides, hasSelectedIdea, houseCanon, inspirationRunning, isAiTrack, isOperator, item, itemMode, jobRunning, layoutPlanM, layoutPlanRunning, layoutQa, layoutQaM, layoutQaRunning, listQ, llmIs, mergeCopySlides, messengerHits, musicStatusQ, note, openExisting, pain, patchCopyM, pauseM, pickTrack, proposeMusicQ, publishBusy, publishRunning, qaBlocks, qaFailed, qaLabel, qaOverride, qc, rankingQ, ratingM, recCta, recomposeM, syncSlideCopyM, reelM, reelRunning, renderM, renderRunning, resetStudio, retroM, retroRunning, retryPublishM, reviseCopyM, applyCopyReviewM, copyChatM, copyChatUndoM, copyReviewOnDemandM, scheduleM, searchMusicM, selectM, selectedId, selectedIdea, setAbPairId, setAbVariable, setAiJob, setBgHint, setBgPresetId, setBgPlacementId, setStyleApplyMode, setBgMode, setBook, setBorrow, setCaption, setCaptionFeedback, setCloseMode, setComposeIndex, setComposeMode, setCopyFeedback, setCopySlides, setCtaIntent, setCtaTouched, setDraftText, setDraftEditMode, setFinalPreview, setForcePick, setFormatId, setFunnel, setNote, setPain, setQaOverride, setSelectedId, setSku, setStudioTab, setStyleId, setTool, setTraceId, setTraceOut, setUrls, setUseBook, setUseComments, setUseMessenger, setUseStories, setVerifyCta, setWhen, showPicker, sku, slot, slotQ, sourceHits, sourcesQ, startConcepts, startPost, startCzasotekst, czasotekstCopyM, statusQ, storyHits, studio, studioTab, styleId, visualPackId, setVisualPackId, templateM, tool, traceId, traceItem, traceOut, uploadInspM, urls, useBook, useComments, useMessenger, useStories, verifyCta, voiceHits, when }} />
        <DraftTraceTab ctx={{ BORROW_CHIPS, DEFAULT_BORROW, STATUS_LABEL, abPairId, abVariable, abortLlmM, activeMode, aiAbort, aiError, aiFailed, aiJob, aiJobParams, aiStatusLabel, analyzeInspM, approveM, askDelete, bgHint, bgPresetId, bgPlacementId, styleApplyMode, bgMode, book, bookHits, bookSectionHits, borrow, borrowM, briefBody, cancelRenderM, caption, captionFeedback, captionJobSteps, captionM, captionChatUndoM, captionOnlyJob, captionRunning, catalog, catalogLabel, catalogQ, catalogTypes, changeTrack, clickAi, closeMode, commentHits, commitCopyField, composeIndex, composeMode, composeModeM, composeSlide, conceptsRunning, copyApproved, copyBusy, copyFeedback, copyMdRef, copyReviseRunning, copyRunning, copySlides, createM, ctaIntent, ctaM, ctaTouched, painPicked, formatPicked, funnelPicked, skuPicked, stylePicked, setPainPicked, setFormatPicked, setFunnelPicked, setSkuPicked, setStylePicked, czasotekstFormId, setCzasotekstFormId, intentKind, setIntentKind, effectiveKind, currentQ, deleteM, designApplyM, designApplyRunning, designFeedbackM, designFixM, designFixRunning, designQa, designReviewM, designReviewRunning, draftText, draftEditMode, effectiveClose, effectiveIntent, finalPreview, forcePick, formatId, fromDraftM, fromDraftRunning, fromPubM, funnel, generateCopyM, goTab, hasComposedSlides, hasCopySlides, hasSelectedIdea, houseCanon, inspirationRunning, isAiTrack, isOperator, item, itemMode, jobRunning, layoutPlanM, layoutPlanRunning, layoutQa, layoutQaM, layoutQaRunning, listQ, llmIs, mergeCopySlides, messengerHits, musicStatusQ, note, openExisting, pain, patchCopyM, pauseM, pickTrack, proposeMusicQ, publishBusy, publishRunning, qaBlocks, qaFailed, qaLabel, qaOverride, qc, rankingQ, ratingM, recCta, recomposeM, syncSlideCopyM, reelM, reelRunning, renderM, renderRunning, resetStudio, retroM, retroRunning, retryPublishM, reviseCopyM, applyCopyReviewM, copyChatM, copyChatUndoM, copyReviewOnDemandM, scheduleM, searchMusicM, selectM, selectedId, selectedIdea, setAbPairId, setAbVariable, setAiJob, setBgHint, setBgPresetId, setBgPlacementId, setStyleApplyMode, setBgMode, setBook, setBorrow, setCaption, setCaptionFeedback, setCloseMode, setComposeIndex, setComposeMode, setCopyFeedback, setCopySlides, setCtaIntent, setCtaTouched, setDraftText, setDraftEditMode, setFinalPreview, setForcePick, setFormatId, setFunnel, setNote, setPain, setQaOverride, setSelectedId, setSku, setStudioTab, setStyleId, setTool, setTraceId, setTraceOut, setUrls, setUseBook, setUseComments, setUseMessenger, setUseStories, setVerifyCta, setWhen, showPicker, sku, slot, slotQ, sourceHits, sourcesQ, startConcepts, startPost, startCzasotekst, czasotekstCopyM, statusQ, storyHits, studio, studioTab, styleId, visualPackId, setVisualPackId, templateM, tool, traceId, traceItem, traceOut, uploadInspM, urls, useBook, useComments, useMessenger, useStories, verifyCta, voiceHits, when }} />
        <CopyTab ctx={{ BORROW_CHIPS, DEFAULT_BORROW, STATUS_LABEL, abPairId, abVariable, abortLlmM, activeMode, aiAbort, aiError, aiFailed, aiJob, aiJobParams, aiStatusLabel, analyzeInspM, approveM, askDelete, bgHint, bgPresetId, bgPlacementId, styleApplyMode, bgMode, book, bookHits, bookSectionHits, borrow, borrowM, briefBody, cancelRenderM, caption, captionFeedback, captionJobSteps, captionM, captionChatUndoM, captionOnlyJob, captionRunning, catalog, catalogLabel, catalogQ, catalogTypes, changeTrack, clickAi, closeMode, commentHits, commitCopyField, composeIndex, composeMode, composeModeM, composeSlide, conceptsRunning, copyApproved, copyBusy, copyFeedback, copyMdRef, copyReviseRunning, copyRunning, copySlides, createM, ctaIntent, ctaM, ctaTouched, painPicked, formatPicked, funnelPicked, skuPicked, stylePicked, setPainPicked, setFormatPicked, setFunnelPicked, setSkuPicked, setStylePicked, czasotekstFormId, setCzasotekstFormId, intentKind, setIntentKind, effectiveKind, currentQ, deleteM, designApplyM, designApplyRunning, designFeedbackM, designFixM, designFixRunning, designQa, designReviewM, designReviewRunning, draftText, draftEditMode, effectiveClose, effectiveIntent, finalPreview, forcePick, formatId, fromDraftM, fromDraftRunning, fromPubM, funnel, generateCopyM, goTab, hasComposedSlides, hasCopySlides, hasSelectedIdea, houseCanon, inspirationRunning, isAiTrack, isOperator, item, itemMode, jobRunning, layoutPlanM, layoutPlanRunning, layoutQa, layoutQaM, layoutQaRunning, listQ, llmIs, mergeCopySlides, messengerHits, musicStatusQ, note, openExisting, pain, patchCopyM, pauseM, pickTrack, proposeMusicQ, publishBusy, publishRunning, qaBlocks, qaFailed, qaLabel, qaOverride, qc, rankingQ, ratingM, recCta, recomposeM, syncSlideCopyM, reelM, reelRunning, renderM, renderRunning, resetStudio, retroM, retroRunning, retryPublishM, reviseCopyM, applyCopyReviewM, copyChatM, copyChatUndoM, copyReviewOnDemandM, scheduleM, searchMusicM, selectM, selectedId, selectedIdea, setAbPairId, setAbVariable, setAiJob, setBgHint, setBgPresetId, setBgPlacementId, setStyleApplyMode, setBgMode, setBook, setBorrow, setCaption, setCaptionFeedback, setCloseMode, setComposeIndex, setComposeMode, setCopyFeedback, setCopySlides, setCtaIntent, setCtaTouched, setDraftText, setDraftEditMode, setFinalPreview, setForcePick, setFormatId, setFunnel, setNote, setPain, setQaOverride, setSelectedId, setSku, setStudioTab, setStyleId, setTool, setTraceId, setTraceOut, setUrls, setUseBook, setUseComments, setUseMessenger, setUseStories, setVerifyCta, setWhen, showPicker, sku, slot, slotQ, sourceHits, sourcesQ, startConcepts, startPost, startCzasotekst, czasotekstCopyM, statusQ, storyHits, studio, studioTab, styleId, visualPackId, setVisualPackId, templateM, tool, traceId, traceItem, traceOut, uploadInspM, urls, useBook, useComments, useMessenger, useStories, verifyCta, voiceHits, when }} />
        <RenderTab ctx={{ BORROW_CHIPS, DEFAULT_BORROW, STATUS_LABEL, abPairId, abVariable, abortLlmM, activeMode, aiAbort, aiError, aiFailed, aiJob, aiJobParams, aiStatusLabel, analyzeInspM, approveM, askDelete, bgHint, bgPresetId, bgPlacementId, styleApplyMode, bgMode, book, bookHits, bookSectionHits, borrow, borrowM, briefBody, cancelRenderM, caption, captionFeedback, captionJobSteps, captionM, captionChatUndoM, captionOnlyJob, captionRunning, catalog, catalogLabel, catalogQ, catalogTypes, changeTrack, clickAi, closeMode, commentHits, commitCopyField, composeIndex, composeMode, composeModeM, composeSlide, conceptsRunning, copyApproved, copyBusy, copyFeedback, copyMdRef, copyReviseRunning, copyRunning, copySlides, createM, ctaIntent, ctaM, ctaTouched, painPicked, formatPicked, funnelPicked, skuPicked, stylePicked, setPainPicked, setFormatPicked, setFunnelPicked, setSkuPicked, setStylePicked, czasotekstFormId, setCzasotekstFormId, intentKind, setIntentKind, effectiveKind, currentQ, deleteM, designApplyM, designApplyRunning, designFeedbackM, designFixM, designFixRunning, designQa, designReviewM, designReviewRunning, draftText, draftEditMode, effectiveClose, effectiveIntent, finalPreview, forcePick, formatId, fromDraftM, fromDraftRunning, fromPubM, funnel, generateCopyM, goTab, hasComposedSlides, hasCopySlides, hasSelectedIdea, houseCanon, inspirationRunning, isAiTrack, isOperator, item, itemMode, jobRunning, layoutPlanM, layoutPlanRunning, layoutQa, layoutQaM, layoutQaRunning, listQ, llmIs, mergeCopySlides, messengerHits, musicStatusQ, note, openExisting, pain, patchCopyM, pauseM, pickTrack, proposeMusicQ, publishBusy, publishRunning, qaBlocks, qaFailed, qaLabel, qaOverride, qc, rankingQ, ratingM, recCta, recomposeM, syncSlideCopyM, reelM, reelRunning, renderM, renderRunning, resetStudio, retroM, retroRunning, retryPublishM, reviseCopyM, applyCopyReviewM, copyChatM, copyChatUndoM, copyReviewOnDemandM, scheduleM, searchMusicM, selectM, selectedId, selectedIdea, setAbPairId, setAbVariable, setAiJob, setBgHint, setBgPresetId, setBgPlacementId, setStyleApplyMode, setBgMode, setBook, setBorrow, setCaption, setCaptionFeedback, setCloseMode, setComposeIndex, setComposeMode, setCopyFeedback, setCopySlides, setCtaIntent, setCtaTouched, setDraftText, setDraftEditMode, setFinalPreview, setForcePick, setFormatId, setFunnel, setNote, setPain, setQaOverride, setSelectedId, setSku, setStudioTab, setStyleId, setTool, setTraceId, setTraceOut, setUrls, setUseBook, setUseComments, setUseMessenger, setUseStories, setVerifyCta, setWhen, showPicker, sku, slot, slotQ, sourceHits, sourcesQ, startConcepts, startPost, startCzasotekst, czasotekstCopyM, statusQ, storyHits, studio, studioTab, styleId, visualPackId, setVisualPackId, templateM, tool, traceId, traceItem, traceOut, uploadInspM, urls, useBook, useComments, useMessenger, useStories, verifyCta, voiceHits, when }} />
        <PublishTab ctx={{ BORROW_CHIPS, DEFAULT_BORROW, STATUS_LABEL, abPairId, abVariable, abortLlmM, activeMode, aiAbort, aiError, aiFailed, aiJob, aiJobParams, aiStatusLabel, analyzeInspM, approveM, askDelete, bgHint, bgPresetId, bgPlacementId, styleApplyMode, bgMode, book, bookHits, bookSectionHits, borrow, borrowM, briefBody, cancelRenderM, caption, captionFeedback, captionJobSteps, captionM, captionChatUndoM, captionOnlyJob, captionRunning, catalog, catalogLabel, catalogQ, catalogTypes, changeTrack, clickAi, closeMode, commentHits, commitCopyField, composeIndex, composeMode, composeModeM, composeSlide, conceptsRunning, copyApproved, copyBusy, copyFeedback, copyMdRef, copyReviseRunning, copyRunning, copySlides, createM, ctaIntent, ctaM, ctaTouched, painPicked, formatPicked, funnelPicked, skuPicked, stylePicked, setPainPicked, setFormatPicked, setFunnelPicked, setSkuPicked, setStylePicked, czasotekstFormId, setCzasotekstFormId, intentKind, setIntentKind, effectiveKind, currentQ, deleteM, designApplyM, designApplyRunning, designFeedbackM, designFixM, designFixRunning, designQa, designReviewM, designReviewRunning, draftText, draftEditMode, effectiveClose, effectiveIntent, fbPublishMode, finalPreview, forcePick, formatId, fromDraftM, fromDraftRunning, fromPubM, funnel, generateCopyM, goTab, hasComposedSlides, hasCopySlides, hasSelectedIdea, houseCanon, inspirationRunning, isAiTrack, isOperator, item, itemMode, jobRunning, layoutPlanM, layoutPlanRunning, layoutQa, layoutQaM, layoutQaRunning, listQ, llmIs, mergeCopySlides, messengerHits, musicStatusQ, note, openExisting, pain, patchCopyM, pauseM, pickTrack, proposeMusicQ, publishBusy, publishChannel, publishRunning, qaBlocks, qaFailed, qaLabel, qaOverride, qc, rankingQ, ratingM, recCta, recomposeM, syncSlideCopyM, reelM, reelRunning, renderM, renderRunning, republishM, resetStudio, retroM, retroRunning, retryPublishM, reviseCopyM, applyCopyReviewM, copyChatM, copyChatUndoM, copyReviewOnDemandM, scheduleM, publishPlatformM, searchMusicM, selectM, selectedId, selectedIdea, setAbPairId, setAbVariable, setAiJob, setBgHint, setBgPresetId, setBgPlacementId, setStyleApplyMode, setBgMode, setBook, setBorrow, setCaption, setCaptionFeedback, setCloseMode, setComposeIndex, setComposeMode, setCopyFeedback, setCopySlides, setCtaIntent, setCtaTouched, setDraftText, setDraftEditMode, setFbPublishMode, setFinalPreview, setForcePick, setFormatId, setFunnel, setNote, setPain, setPublishChannel, setQaOverride, setSelectedId, setSku, setStudioTab, setStyleId, setTool, setTraceId, setTraceOut, setUrls, setUseBook, setUseComments, setUseMessenger, setUseStories, setVerifyCta, setWhen, showPicker, sku, slot, slotQ, sourceHits, sourcesQ, startConcepts, startPost, startCzasotekst, czasotekstCopyM, statusQ, storyHits, studio, studioTab, styleId, visualPackId, setVisualPackId, templateM, tool, traceId, traceItem, traceOut, uploadInspM, urls, useBook, useComments, useMessenger, useStories, verifyCta, voiceHits, when }} />
        <RetroTab ctx={{ item, selectedId, retroM, clickAi, retroRunning, goTab }} />
        <WynikiTab ctx={{ item, selectedId, retroM, clickAi, retroRunning, goTab }} />
      </Tabs>
      </div>

      <AiJobModal
        open={Boolean(
          aiJob &&
            aiJob !== "fromDraft" &&
            aiJob !== "layoutPlan" &&
            aiJob !== "publish" &&
            aiJob !== "render" &&
            aiJob !== "houseFix" &&
            aiJob !== "designReview" &&
            aiJob !== "designApply" &&
            aiJob !== "layoutQa",
        )}
        kind={aiJob}
        item={traceItem || item}
        params={aiJobParams()}
        outgoing={traceOut}
        running={Boolean(aiJob && jobRunning(aiJob))}
        failed={aiFailed}
        statusLabel={aiStatusLabel()}
        error={aiError}
        abortPending={aiJob === "render" ? cancelRenderM.isPending : abortLlmM.isPending}
        onAbort={aiAbort}
        onClose={() => setAiJob(null)}
        applyPending={designApplyM.isPending}
        onApplyVerdict={(selected) => {
          clickAi("designApply", () => designApplyM.mutate(selected));
        }}
        copyReviewPending={applyCopyReviewM.isPending}
        copyReviewError={applyCopyReviewM.isError ? (applyCopyReviewM.error as Error)?.message : ""}
        onApplyCopyReview={(actionIds, rejectIds) => {
          applyCopyReviewM.mutate({ actionIds, rejectIds });
        }}
      />

      {finalPreview === "approve" && selectedId && item && (
        <FinalSlidesModal
          item={item}
          mode="approve"
          baking={recomposeM.isPending}
          bakeError={recomposeM.error instanceof Error ? recomposeM.error.message : ""}
          confirmPending={approveM.isPending}
          confirmError={approveM.error instanceof Error ? approveM.error.message : ""}
          qaBlocked={qaBlocks}
          qaFailed={qaFailed}
          qaOverride={qaOverride}
          qaLabel={qaLabel}
          fixPending={layoutQaM.isPending}
          onQaOverride={setQaOverride}
          onFixLayout={() => layoutQaM.mutate()}
          onClose={() => setFinalPreview(null)}
          onConfirm={() => {
            if (qaBlocks) return;
            approveM.mutate();
          }}
        />
      )}
    </div>
  );
}
